import { ChipIcon, CodeIcon } from '@heroicons/react/outline';
import React from 'react';
import {
  FaDiscord,
  FaEnvelope,
  FaFacebookMessenger,
  FaGoogle,
  FaLine,
  FaPhone,
  FaShopify,
  FaSlack,
  FaTelegram,
  FaWhatsapp,
  FaWordpress,
} from 'react-icons/fa6';

import clsxm from '@/lib/clsxm';

import { CONNECTOR_ICONS } from '@/components/Chat/connectors/icons';

import ZapierSVG from '~/svg/Zapier.svg';

/**
 * The API returns logos as either a react-icons name ("FaSlack"), a public
 * asset path ("/images/integrations/basecamp.svg") or nothing at all. This
 * resolves any of those into a node rendered at a consistent size.
 */
export const resolveChannelIcon = (
  logo?: string,
  className = 'h-6 w-6',
): React.ReactNode => {
  switch (logo) {
    case 'FaDiscord':
      return <FaDiscord className={clsxm(className, 'text-[#5865F2]')} />;
    case 'FaTelegram':
      return <FaTelegram className={clsxm(className, 'text-[#0088cc]')} />;
    case 'FaWhatsapp':
      return <FaWhatsapp className={clsxm(className, 'text-[#25D366]')} />;
    case 'FaFacebookMessenger':
      return (
        <FaFacebookMessenger className={clsxm(className, 'text-[#0084FF]')} />
      );
    case 'FaLine':
      return <FaLine className={clsxm(className, 'text-[#06C755]')} />;
    case 'FaSlack':
      return <FaSlack className={clsxm(className, 'text-[#4A154B]')} />;
    case 'FaPhone':
      return <FaPhone className={clsxm(className, 'text-[#F22F46]')} />;
    case 'FaGoogle':
      return <FaGoogle className={clsxm(className, 'text-[#4285F4]')} />;
    case 'FaShopify':
      return <FaShopify className={clsxm(className, 'text-[#95BF47]')} />;
    case 'FaEnvelope':
      return <FaEnvelope className={clsxm(className, 'text-[#6366F1]')} />;
    case 'Gmail':
      return CONNECTOR_ICONS.gmail(className);
    case 'Outlook':
      return CONNECTOR_ICONS['outlook-mail'](className);
    case 'FaWordpress':
      return <FaWordpress className={clsxm(className, 'text-[#21759B]')} />;
    case 'Zapier':
      return <ZapierSVG className={clsxm(className, 'fill-[#FF4A00]')} />;
    case 'ChipIcon':
      return <ChipIcon className={clsxm(className, 'text-primary-600')} />;
    default:
      if (logo && logo.startsWith('/')) {
        return (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={logo}
            alt=''
            className={clsxm(className, 'object-contain')}
          />
        );
      }
      return <CodeIcon className={clsxm(className, 'text-primary-600')} />;
  }
};

/** Rounded tile the icon sits in — the visual anchor of every catalog card. */
export const LogoTile = ({
  logo,
  icon,
  size = 'md',
}: {
  logo?: string;
  icon?: React.ReactNode;
  size?: 'md' | 'lg';
}) => (
  <span
    className={clsxm(
      'flex shrink-0 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-gray-900/5',
      'dark:bg-slate-900 dark:ring-white/10',
      size === 'lg' ? 'h-14 w-14' : 'h-11 w-11',
    )}
  >
    {icon ?? resolveChannelIcon(logo, size === 'lg' ? 'h-7 w-7' : 'h-6 w-6')}
  </span>
);
